import { Organization } from "fhir/r4";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { Button, Form, InputGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

/**
 * Has to be used inside a Form component. Use configuration displayed below to enable validation.
 * 
 * <Form noValidate validated={validated}>
 * 
 * @param props properties for the component.
 * @returns the rendered Elements.
 */
const OrganizationForm: FunctionComponent<{
    submitTrigger?: boolean,
    resetTrigger?: boolean,
    displayButtons?: boolean,
    organizationSubmit: (organization: Organization) => void
}> = (props) => {
    const { t } = useTranslation();
    const submitOrganizationRef = useRef<() => void>(() => undefined);
    submitOrganizationRef.current = generateAndSubmitOrganization;

    useEffect(() => {
        if (props.resetTrigger) {
            onReset();
        }
    }, [props.resetTrigger]);

    useEffect(() => {
        if (props.submitTrigger) {
            submitOrganizationRef.current();
        }
    }, [props.submitTrigger]);

    const [form, setForm] = useState({
        name: '',
        type: '',
        phone: '',
        line: '',
        city: '',
        postal_code: '',
    });

    const handleChange = (event: { target: { name: any; value: any; }; }) => {
        setForm({ ...form, [event.target.name]: event.target.value });
    }

    const onSubmit = (event: any) => {
        const formtv = event.currentTarget;
        if (formtv.checkValidity() === true) {
            generateAndSubmitOrganization();
        }
    }

    function generateAndSubmitOrganization() {
        const organization = {
            "resourceType": "Organization",
            "name": form.name,
            "type": [
                {
                  "coding": [
                    {
                      "system": "http://interopsante.org/fhir/CodeSystem/fr-v2-3307",
                      "code": form.type
                    }
                  ]
                }
              ],
              "contact": [
                {
                  "telecom": [
                    {
                      "system": "phone",
                      "value": form.phone,
                      "use": "work"
                    }
                  ]
                }
              ],
              "address": [
                {
                  "line": [
                    form.line
                  ],
                  "city": form.city,
                  "postalCode": form.postal_code,
                  "country": "FRA"
                }
              ]
        } as Organization;

        props.organizationSubmit(organization);
    }

    const onReset = () => {
        setForm({
            name: '',
            type: '',
            phone: '',
            line: '',
            city: '',
            postal_code: '',
        });
    }

    return <><Form.Group
        className="col"
    >
        <Form.Label>{t("forms.common.name")} :</Form.Label>
        <Form.Control
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            className="form-control"
            required />
        <Form.Control.Feedback type="invalid">
            {t("forms.organization.invalidName")}
        </Form.Control.Feedback>
    </Form.Group>
        <Form.Group className="col">
            <Form.Label>{t("forms.organization.type")} :</Form.Label>
            <Form.Select
                className="form-control"
                name="type"
                value={form.type}
                onChange={handleChange}
                required
            >
                <option value="">{t("forms.common.choose")}</option>
                {(["GHT", "LEGAL-ENTITY", "GEOGRAPHICAL-ENTITY", "GROUP", "HEBERGEMENT", "SOIN", "ADMINISTRATIF", "MEDICAL", "TECHNIQUE", "MEDICOTEC", "UF", "SERVICE", "UM", "UAC", "POLE", "CENTRE-RESP", "CENTRE-ACTIVITE"] as const).map((type) => <option key={type} value={type}>{t(`forms.organization.types.${type}`)}</option>)}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
                {t("forms.organization.invalidType")}
            </Form.Control.Feedback>
        </Form.Group>


        <Row className="mb-3">
            <Form.Group className="col">
                <Form.Label>{t("forms.organization.address")} :</Form.Label>
                <InputGroup hasValidation>
                    <Form.Control
                        type="text"
                        name="line"
                        value={form.line}
                        onChange={handleChange}
                        placeholder="1 rue de Paris"
                        required />
                    <Form.Control
                        type="text"
                        name="city"
                        value={form.city}
                        onChange={handleChange}
                        placeholder="Rennes"
                        required />
                    <Form.Control
                        type="text"
                        name="postal_code"
                        value={form.postal_code}
                        onChange={handleChange}
                        placeholder="35000"
                        required />
                </InputGroup>
            </Form.Group>
        </Row>

        <Form.Group
            className="col"
        >
            <Form.Label>{t("forms.common.phone")} :</Form.Label>
            <Form.Control
                type="phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="form-control"
                required />
            <Form.Control.Feedback type="invalid">
                {t("forms.organization.invalidPhone")}
            </Form.Control.Feedback>
        </Form.Group>

        {props.displayButtons &&
            <Row className="mb-3">
                <Form.Group className="col">

                    <Button
                        className="button"
                        variant="danger"
                        onClick={onSubmit}
                    >
                        {t("forms.common.submit")}
                    </Button>
                    <Button
                        className="button"
                        variant="secondary"
                        onClick={onReset}
                    >
                        {t("forms.common.reset")}
                    </Button>
                </Form.Group>
            </Row>
        }
    </>;
};

export default OrganizationForm;
